<template>
  <div
    :class="{ 'visible': isOpen }"
    class="modal-wrapper"
  >
    <div class="modal">
      <div class="modal-header">
        <h2 class="is-size-5">
          Add Division
        </h2>
      </div>
      <div class="modal-body">
        <div
          v-if="!simpleCreate"
          class="columns"
        >
          <div class="column">
            <div class="tabs is-justified">
              <ul>
                <li :class="{ 'is-active': currentTab === 'new' }">
                  <a
                    href="#"
                    @click.prevent="toggleTab('new')"
                  >Create</a>
                </li>
                <li :class="{ 'is-active': currentTab === 'existing' }">
                  <a
                    href="#"
                    @click.prevent="toggleTab('existing')"
                  >Select Existing</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div class="tabs-content">
          <div
            v-if="currentTab === 'new'"
          >
            <div class="columns">
              <div class="column is-full">
                <TextField
                  :label="'Name'"
                  :placeholder="'Division name'"
                  :name="'eventname'"
                  :initial-value="name"
                  :message="nameMessage"
                  :extraClass="nameMessage ? nameMessage.class : ''"
                  @changedValue="saveName"
                />
              </div>
            </div>
            <div class="columns">
              <div class="column is-full">
                <CurrencyField
                  :label="'Price'"
                  :placeholder="'$10.99'"
                  :name="'eventname'"
                  :initial-value="price"
                  :message="priceMessage"
                  :extraClass="priceMessage ? priceMessage.class : ''"
                  @changedValue="savePrice"
                />
              </div>
            </div>
          </div>
          <div
            v-if="currentTab === 'existing'"
          >
            <div class="columns">
              <div
                v-if="loadingDivisions"
                class="column divisions-wrapper smaller"
              >
                Loading...
              </div>
              <div
                v-else
                class="column divisions-wrapper smaller"
              >
                <span v-if="selectedDivisions.length > 0">
                  <DivisionItem
                    v-for="division in selectedDivisions"
                    :key="division._id"
                    :division="division"
                    @toggleDivision="toggleDivision"
                  />
                </span>
                <div
                  v-else
                  class="has-text-centered"
                >
                  No divisions yet
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <a
          href="#"
          class="button is-light"
          @click.prevent="closeModal(false)"
        >Cancel</a>
        <a
          href="#"
          :class="{ 'is-loading': submitting }"
          class="button is-warning has-text-weight-bold"
          :disabled="!isValid || submitting"
          @click.prevent="submitForm"
        >
          Save
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import TextField from '@/components/inputs/TextField'
import CurrencyField from '@/components/inputs/CurrencyField'
import DivisionItem from '@/components/registerFormSteps/DivisionItem'

import inputMessages from '@/configs/inputMessages'

export default {
  name: 'EventModal',
  components: {
    TextField,
    CurrencyField,
    DivisionItem
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'create'
    },
    initialData: {
      type: [Object, Boolean],
      default: false
    }
  },
  data () {
    return {
      //  Values
      eventId: '',
      name: '',
      price: 0,
      selectedDivisions: [],
      simpleCreate: false,
      //  Errors
      nameMessage: false,
      priceMessage: false,
      //  State
      submitting: false,
      currentTab: 'new',
      loadingDivisions: false
    }
  },
  mounted () {
    if (typeof this.initialData === 'object') {
      this.populateFields()
      setTimeout(() => {
        this.validateFields()
      }, 250)
    } else if (this.initialData) {
      //  True indicates we are creating a new Division, decoupled from any Event
      this.simpleCreate = true
    }
  },
  computed: {
    isValid () {
      return this.validateFields()
    }
  },
  watch: {
    currentTab () {
      if (this.currentTab === 'existing') {
        this.loadDivisions()
      }
    }
  },
  methods: {
    toggleTab (value) {
      this.currentTab = value
    },
    saveName (value) {
      if (value.trim().length > 0) {
        this.name = value.trim()
        this.nameMessage = false
      } else {
        this.nameMessage = inputMessages.required
        this.name = value
      }
    },
    savePrice (value) {
      if (value > 0) {
        this.price = value
      }
    },
    toggleDivision (params) {
      const i = this.selectedDivisions.findIndex(d => {
        return d._id === params._id
      })

      let d = this.selectedDivisions[i]
      d.selected = params.selected
      this.selectedDivisions.splice(i, 1, d)
      this.validateFields()
    },
    validateFields () {
      let valid = false
      if (this.currentTab === 'new') {
        if (
          this.name.length > 0 &&
          this.price > 0
        ) {
          valid = true
        }
      } else {
        let selected = this.selectedDivisions.filter(division => {
          return division.selected
        })
        if (selected.length > 0) {
          valid = true
        }
      }
      return valid
    },
    submitForm () {
      if (this.isValid) {
        switch (this.currentTab) {
          case 'new':
            this.saveNewDivision()
            break
          case 'existing':
            this.addExistingDivision()
            break
        }
      }
    },
    saveNewDivision () {
      let data = {
        name: this.name,
        price: this.price
      }

      if (this.eventId.length > 0) {
        data['event'] = this.eventId
      }

      this.$store
        .dispatch('admin/SAVE_DIVISION', data)
        .then(res => {
          this.submitting = false
          this.closeModal(true)
          this.$toasted.success('Added new division')
        })
        .catch(err => {
          this.submitting = false
          this.$toasted.error('Error saving new event')
          console.log(err)
        })
    },
    addExistingDivision () {
      let divisions = []

      this.selectedDivisions.map(division => {
        if (division.selected) {
          divisions.push(division._id)
        }
      })

      let data = {
        match: {
          _id: this.eventId
        },
        update: {
          divisions: divisions
        }
      }

      this.$store
        .dispatch('admin/UPDATE_EVENT_DIVISION', data)
        .then(res => {
          this.submitting = false
          this.closeModal(true)
          this.$toasted.success('Added division(s)')
        })
        .catch(err => {
          this.submitting = false
          this.$toasted.error('Error adding division(s)')
          console.log(err)
        })
    },
    closeModal (refresh) {
      this.$emit('closeModal', refresh)
    },
    populateFields () {
      if ('_id' in this.initialData) {
        this.eventId = this.initialData._id
      }
      if ('name' in this.initialData) {
        this.name = this.initialData.name
      }
      if ('price' in this.initialData) {
        this.price = this.initialData.price
      }
    },
    loadDivisions () {
      this.loadingDivisions = true
      this.$store
        .dispatch('admin/GET_DIVISION')
        .then(res => {
          if (res.data.divisions.length > 0) {
            res.data.divisions.map(division => {
              division.selected = false
            })
          }

          return res.data.divisions
        })
        .then(data => {
          this.selectedDivisions = data
          this.loadingDivisions = false
        })
    }
  }
}
</script>

<style lang="scss">
  .tabs.is-justified{
    li {
      width: 50%;
    }
  }
</style>
